import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import Dotdotdot from 'react-dotdotdot';
import { css, styled } from 'styled-components';
import { useIsMounted } from '@hivebrite/react-hooks';
import { Box } from "../../box/src";
import { Card } from "../../card/src";
import { CATEGORIES, FontAwesomeIcon } from "../../font-awesome-icon/src";
import { useBreakpoint } from "../../theme/src";
import { STATIC_MAP_URL, styles } from './constant';
import { StaticGoogleMapLoading } from './StaticGoogleMapLoading';
import { StaticGoogleMapMessage } from './StaticGoogleMapMessage';
import { getStylesInFormat } from './utils';
export const StaticGoogleMap = ({ apiKey, dataTestId = 'static-google-map', enableBorder = false, enableBorderRadius = true, enablePreview = false, errorMessage, footer, latitude, longitude, previewMessage, zoom, }) => {
    const breakpoint = useBreakpoint();
    const isMounted = useIsMounted();
    const [customDataTestId, setCustomDataTestId] = useState(dataTestId);
    const [source, setSource] = useState('');
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [height, setHeight] = useState(0);
    const cardRef = useRef();
    const customStyles = getStylesInFormat(styles);
    const loadImage = useCallback(() => {
        if (latitude && longitude && (cardRef === null || cardRef === void 0 ? void 0 : cardRef.current) && height > 0) {
            const rect = cardRef.current.getBoundingClientRect();
            const width = Math.floor(rect.width);
            const src = `${STATIC_MAP_URL}?scale=2&center=${latitude},${longitude}&zoom=${zoom}&size=${width}x${height}&markers=size:mid%7C${latitude},${longitude}&${customStyles}&key=${apiKey}`;
            setSource(src);
            const image = document.createElement('img');
            image.src = src;
            image.onload = function () {
                if (isMounted()) {
                    setIsLoaded(true);
                }
            };
            image.onerror = function () {
                if (isMounted()) {
                    setIsLoaded(true);
                    setHasError(true);
                    setCustomDataTestId('error-message');
                }
            };
        }
    }, [apiKey, customStyles, height, isMounted, latitude, longitude, zoom]);
    useEffect(() => {
        loadImage();
    }, [height, latitude, loadImage, longitude]);
    useEffect(() => {
        setHeight(breakpoint === 's' ? 124 : 172);
    }, [breakpoint]);
    useEffect(() => {
        window.addEventListener('resize', loadImage);
        return () => {
            window.removeEventListener('resize', loadImage);
        };
    }, [loadImage]);
    const displayMap = () => {
        var _a;
        return (_jsxs(_Fragment, { children: [_jsx(Image, { "$enableBorderRadius": enableBorderRadius, "$hasBorderRadiusOnBottom": !footer && enableBorderRadius, "$height": `${height}px`, "$source": source, "data-testid": "static-google-map-image" }), footer && (_jsxs(Box, { "data-testid": "static-google-map-footer", minHeight: "64px", p: 3, children: [_jsx(Box, { children: _jsx(FontAwesomeIcon, { color: "primary", height: "24px", iconName: footer.iconName, iconType: CATEGORIES.DUOTONE, rotation: (_a = footer.rotation) !== null && _a !== void 0 ? _a : 0 }) }), _jsxs(AddressWrapper, { "$hasDateRange": Boolean(footer.dateRange), flexDirection: "column", ml: "9px", children: [_jsx(Box, { font: "body-semibold-asphalt", children: _jsx(Dotdotdot, { clamp: 1, useNativeClamp: true, children: footer.address }) }), footer.dateRange && (_jsxs(Box, { mt: "6px", children: [footer.dateRange[0], " - ", footer.dateRange[1]] }))] })] }))] }));
    };
    const hasPreview = (!latitude || !longitude) && enablePreview;
    const loadedContent = hasError ? (_jsx(StaticGoogleMapMessage, { message: errorMessage })) : (displayMap());
    const content = isLoaded ? (loadedContent) : (_jsx(StaticGoogleMapLoading, { hasFooter: Boolean(footer) }));
    const display = hasPreview ? (_jsx(StaticGoogleMapMessage, { message: previewMessage })) : (content);
    return (_jsx(CardWrapper, { "$enableBorder": enableBorder, "$enableBorderRadius": enableBorderRadius, "data-testid": customDataTestId, flexDirection: "column", p: 0, ref: cardRef, width: "100%", children: display }));
};
const CardWrapper = styled(Card) `
  ${({ $enableBorder, theme }) => $enableBorder &&
    css `
      border: 1px solid ${theme.colors.dove};
    `}

  ${({ $enableBorderRadius }) => !$enableBorderRadius &&
    css `
      border-radius: 0;
    `}
`;
const Image = styled(Box) `
  ${({ $height, $source }) => $source &&
    css `
      width: 100%;
      height: ${$height};
      background-repeat: no-repeat;
      background-position: center;
      background: url('${$source}');
      background-size: cover;
      border-top-left-radius: ${({ $enableBorderRadius, theme }) => $enableBorderRadius
        ? `${theme.themeVariables.globalBorderRadius}px`
        : 0};
      border-top-right-radius: ${({ $enableBorderRadius, theme }) => $enableBorderRadius
        ? `${theme.themeVariables.globalBorderRadius}px`
        : 0};

      ${({ $hasBorderRadiusOnBottom }) => $hasBorderRadiusOnBottom &&
        css `
          border-bottom-left-radius: ${({ theme }) => `${theme.themeVariables.globalBorderRadius}px`};
          border-bottom-right-radius: ${({ theme }) => `${theme.themeVariables.globalBorderRadius}px`};
        `}
    `}
`;
const AddressWrapper = styled(Box) `
  ${({ $hasDateRange }) => !$hasDateRange &&
    css `
      height: 24px;
      justify-content: center;
    `}
`;
//# sourceMappingURL=StaticGoogleMap.js.map